<template>
  <div v-if="visible" class="tour-root">
    <div class="tour-backdrop" @click="finish"></div>

    <div
      v-if="hasTarget"
      class="tour-highlight"
      :style="highlightStyle"
      aria-hidden="true"
    ></div>

    <v-card class="tour-card" :style="cardStyle" elevation="14">
      <div class="tour-card__header">
        <v-chip x-small color="primary" dark>
          {{ stepCounter }}
        </v-chip>
        <v-btn icon small :aria-label="$t('tutorial.close')" @click="finish">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-title class="tour-title">
        {{ currentStep.title }}
      </v-card-title>

      <v-card-text class="tour-text">
        {{ currentStep.text }}
      </v-card-text>

      <v-card-actions class="tour-actions">
        <v-btn text small @click="finish">
          {{ $t('tutorial.skip') }}
        </v-btn>
        <v-spacer />
        <v-btn text small :disabled="activeIndex === 0" @click="previousStep">
          <v-icon left small>mdi-chevron-left</v-icon>
          {{ $t('tutorial.previous') }}
        </v-btn>
        <v-btn color="primary" small depressed @click="nextStep">
          {{ isLastStep ? $t('tutorial.finish') : $t('tutorial.next') }}
          <v-icon right small>{{ isLastStep ? 'mdi-check' : 'mdi-chevron-right' }}</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
const storageKey = "konvenio-tour-completed";

export default {
  name: "AppTour",

  props: {
    autoStart: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      visible: false,
      activeIndex: 0,
      targetRect: null,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      steps: [
        {
          selector: "[data-tour='brand']",
          titleKey: "tutorial.steps.brand.title",
          textKey: "tutorial.steps.brand.text",
        },
        {
          selector: "[data-tour='main-nav']",
          titleKey: "tutorial.steps.nav.title",
          textKey: "tutorial.steps.nav.text",
        },
        {
          selector: "[data-tour='nav-notices']",
          route: { name: "notices" },
          titleKey: "tutorial.steps.noticesNav.title",
          textKey: "tutorial.steps.noticesNav.text",
        },
        {
          selector: "[data-tour='notices-create-board']",
          route: { name: "notices" },
          titleKey: "tutorial.steps.noticesCreate.title",
          textKey: "tutorial.steps.noticesCreate.text",
        },
        {
          selector: "[data-tour='nav-agreement']",
          route: { name: "agreement" },
          titleKey: "tutorial.steps.agreementNav.title",
          textKey: "tutorial.steps.agreementNav.text",
        },
        {
          selector: "[data-tour='agreement-create']",
          route: { name: "agreement" },
          titleKey: "tutorial.steps.agreementCreate.title",
          textKey: "tutorial.steps.agreementCreate.text",
        },
        {
          selector: "[data-tour='nav-calendar']",
          route: { name: "calendar" },
          titleKey: "tutorial.steps.calendarNav.title",
          textKey: "tutorial.steps.calendarNav.text",
        },
        {
          selector: "[data-tour='calendar-add-year']",
          route: { name: "calendar" },
          titleKey: "tutorial.steps.calendarAdd.title",
          textKey: "tutorial.steps.calendarAdd.text",
        },
        {
          selector: "[data-tour='nav-workers']",
          route: { name: "workers" },
          titleKey: "tutorial.steps.workersNav.title",
          textKey: "tutorial.steps.workersNav.text",
        },
        {
          selector: "[data-tour='workers-tabs']",
          route: { name: "workers" },
          titleKey: "tutorial.steps.workersTabs.title",
          textKey: "tutorial.steps.workersTabs.text",
        },
        {
          selector: "[data-tour='nav-suggestions']",
          route: { name: "suggestions" },
          titleKey: "tutorial.steps.suggestionsNav.title",
          textKey: "tutorial.steps.suggestionsNav.text",
        },
        {
          selector: "[data-tour='suggestions-refresh']",
          route: { name: "suggestions" },
          titleKey: "tutorial.steps.suggestionsRefresh.title",
          textKey: "tutorial.steps.suggestionsRefresh.text",
        },
        {
          selector: "[data-tour='user-actions']",
          titleKey: "tutorial.steps.account.title",
          textKey: "tutorial.steps.account.text",
        },
        {
          selector: "[data-tour='tour-help']",
          titleKey: "tutorial.steps.help.title",
          textKey: "tutorial.steps.help.text",
        },
      ],
    };
  },

  computed: {
    currentStep() {
      const step = this.steps[this.activeIndex] || this.steps[0];
      return {
        ...step,
        title: this.$t(step.titleKey),
        text: this.$t(step.textKey),
      };
    },

    hasTarget() {
      return !!this.targetRect;
    },

    isLastStep() {
      return this.activeIndex === this.steps.length - 1;
    },

    stepCounter() {
      return `${this.activeIndex + 1}/${this.steps.length}`;
    },

    highlightStyle() {
      if (!this.targetRect) return {};

      return {
        left: `${this.targetRect.left - 8}px`,
        top: `${this.targetRect.top - 8}px`,
        width: `${this.targetRect.width + 16}px`,
        height: `${this.targetRect.height + 16}px`,
      };
    },

    cardStyle() {
      const cardWidth = Math.min(360, this.viewport.width - 32);

      if (!this.targetRect) {
        return {
          left: `${(this.viewport.width - cardWidth) / 2}px`,
          top: "96px",
          width: `${cardWidth}px`,
        };
      }

      const gap = 16;
      const spaceRight = this.viewport.width - this.targetRect.right;
      const spaceLeft = this.targetRect.left;
      const top = Math.min(
        Math.max(16, this.targetRect.top),
        this.viewport.height - 220
      );

      if (spaceRight >= cardWidth + gap) {
        return {
          left: `${this.targetRect.right + gap}px`,
          top: `${top}px`,
          width: `${cardWidth}px`,
        };
      }

      if (spaceLeft >= cardWidth + gap) {
        return {
          left: `${this.targetRect.left - cardWidth - gap}px`,
          top: `${top}px`,
          width: `${cardWidth}px`,
        };
      }

      const belowTop = Math.min(this.targetRect.bottom + gap, this.viewport.height - 220);
      const left = Math.min(
        Math.max(16, this.targetRect.left),
        this.viewport.width - cardWidth - 16
      );

      return {
        left: `${left}px`,
        top: `${Math.max(16, belowTop)}px`,
        width: `${cardWidth}px`,
      };
    },
  },

  mounted() {
    window.addEventListener("resize", this.updateViewport);
    window.addEventListener("scroll", this.updateTargetRect, true);

    if (this.autoStart && localStorage.getItem(storageKey) !== "true") {
      this.start();
    }
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.updateViewport);
    window.removeEventListener("scroll", this.updateTargetRect, true);
  },

  methods: {
    start() {
      this.visible = true;
      this.activeIndex = 0;
      this.prepareStep();
    },

    async prepareStep() {
      const step = this.steps[this.activeIndex];

      if (step.route && this.$route.name !== step.route.name) {
        await this.$router.push(step.route).catch(() => {});
      }

      this.$nextTick(() => {
        window.setTimeout(() => this.positionStep(true), 120);
      });
    },

    positionStep(scrollTarget = false) {
      if (!this.visible) return;

      const target = document.querySelector(this.currentStep.selector);
      if (!target) {
        this.targetRect = null;
        return;
      }

      if (scrollTarget) {
        target.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
      }

      const rect = target.getBoundingClientRect();

      this.targetRect = {
        left: rect.left,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
      };
    },

    updateTargetRect() {
      this.positionStep(false);
    },

    updateViewport() {
      this.viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      this.positionStep(false);
    },

    nextStep() {
      if (this.isLastStep) {
        this.finish();
        return;
      }

      this.activeIndex += 1;
      this.prepareStep();
    },

    previousStep() {
      if (this.activeIndex === 0) return;
      this.activeIndex -= 1;
      this.prepareStep();
    },

    finish() {
      localStorage.setItem(storageKey, "true");
      this.visible = false;
      this.targetRect = null;
    },
  },
};
</script>

<style scoped>
.tour-root {
  inset: 0;
  pointer-events: none;
  position: fixed;
  z-index: 300;
}

.tour-backdrop {
  background: rgba(22, 20, 36, 0.58);
  inset: 0;
  pointer-events: auto;
  position: fixed;
}

.tour-highlight {
  border: 3px solid #4a3aff;
  border-radius: 12px;
  box-shadow: 0 0 0 9999px rgba(22, 20, 36, 0.58), 0 10px 28px rgba(0, 0, 0, 0.24);
  pointer-events: none;
  position: fixed;
  transition: all 0.18s ease;
  z-index: 301;
}

.tour-card {
  border-radius: 8px;
  pointer-events: auto;
  position: fixed;
  transition: left 0.18s ease, top 0.18s ease;
  z-index: 302;
}

.tour-card__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 10px 12px 0;
}

.tour-title {
  color: #29243d;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.25;
  padding: 8px 18px 6px;
  word-break: normal;
}

.tour-text {
  color: #5d5870;
  font-size: 14px;
  line-height: 1.5;
  padding: 0 18px 12px;
}

.tour-actions {
  padding: 8px 12px 14px;
}

@media (max-width: 600px) {
  .tour-card {
    max-width: calc(100vw - 32px);
  }

  .tour-actions {
    flex-wrap: wrap;
    gap: 6px;
  }
}
</style>
